/**
 * StatusMappingService
 * 
 * Provides bidirectional mapping between different status systems used across
 * the College Journey module to ensure data consistency.
 * 
 * Systems:
 * 1. Epic Journey: workflow_phase (ready, in_progress, review, blocked, complete)
 * 2. Calendar/Timeline: status (planned, in_progress, completed)
 * 3. Applications: application_status (not_started, started, in_progress, submitted, completed)
 * 4. Essays: status (draft, in_progress, review, complete)
 */

class StatusMappingService {
  // Mapping between Epic workflow_phase and Calendar status
  static workflowToStatusMap = {
    'ready': 'planned',
    'in_progress': 'in_progress',
    'review': 'in_progress',
    'blocked': 'in_progress',
    'complete': 'completed'
  };

  // Reverse mapping
  static statusToWorkflowMap = {
    'planned': 'ready',
    'in_progress': 'in_progress',
    'completed': 'complete'
  };

  // Application status mapping to workflow phase
  static applicationToWorkflowMap = {
    'not_started': 'ready',
    'started': 'in_progress',
    'in_progress': 'in_progress',
    'submitted': 'review',
    'completed': 'complete'
  };

  // Essay status mapping to workflow phase
  static essayToWorkflowMap = {
    'draft': 'ready',
    'in_progress': 'in_progress',
    'review': 'review',
    'complete': 'complete'
  };

  /**
   * Convert workflow_phase to calendar status
   */
  static workflowToStatus(workflowPhase) {
    return this.workflowToStatusMap[workflowPhase] || 'planned';
  }

  /**
   * Convert calendar status to workflow_phase
   */
  static statusToWorkflow(status) {
    return this.statusToWorkflowMap[status] || 'ready';
  }

  /**
   * Convert application_status to workflow_phase
   */
  static applicationToWorkflow(applicationStatus) {
    return this.applicationToWorkflowMap[applicationStatus] || 'ready';
  }

  /**
   * Convert essay status to workflow_phase
   */
  static essayToWorkflow(essayStatus) {
    return this.essayToWorkflowMap[essayStatus] || 'ready';
  }

  /**
   * Get synchronized status object with all fields
   * This ensures both workflow_phase and status are always in sync
   */
  static getSynchronizedStatus(sourceField, sourceValue) {
    let workflowPhase, status, applicationStatus;

    switch (sourceField) {
      case 'workflow_phase':
        workflowPhase = sourceValue;
        status = this.workflowToStatus(sourceValue);
        // Derive application status if needed
        if (sourceValue === 'ready') applicationStatus = 'not_started';
        else if (sourceValue === 'in_progress') applicationStatus = 'in_progress';
        else if (sourceValue === 'review') applicationStatus = 'submitted';
        else if (sourceValue === 'complete') applicationStatus = 'completed';
        break;

      case 'status':
        status = sourceValue;
        workflowPhase = this.statusToWorkflow(sourceValue);
        // Derive application status
        if (sourceValue === 'planned') applicationStatus = 'not_started';
        else if (sourceValue === 'in_progress') applicationStatus = 'in_progress';
        else if (sourceValue === 'completed') applicationStatus = 'completed';
        break;

      case 'application_status':
        applicationStatus = sourceValue;
        workflowPhase = this.applicationToWorkflow(sourceValue);
        status = this.workflowToStatus(workflowPhase);
        break;

      default:
        // Default to ready/planned state
        return {
          workflow_phase: 'ready',
          status: 'planned',
          application_status: 'not_started'
        };
    }

    return {
      workflow_phase: workflowPhase,
      status: status,
      application_status: applicationStatus
    };
  }

  /**
   * Update task with synchronized status fields
   * Use this when updating any status field to ensure consistency
   */
  static async updateTaskStatus(supabase, taskId, sourceField, sourceValue) {
    const syncedStatus = this.getSynchronizedStatus(sourceField, sourceValue);
    
    const { data, error } = await supabase
      .from('timeline_items')
      .update({
        ...syncedStatus,
        status_updated_at: new Date().toISOString()
      })
      .eq('id', taskId)
      .select()
      .single();

    if (error) {
      console.error('Error updating synchronized status:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get display information for a workflow phase
   */
  static getWorkflowPhaseDisplay(phase) {
    const displays = {
      'ready': { label: 'Ready', color: 'slate', icon: 'Clock' },
      'in_progress': { label: 'In Progress', color: 'azure', icon: 'PlayCircle' },
      'review': { label: 'In Review', color: 'amber', icon: 'Eye' },
      'blocked': { label: 'Blocked', color: 'red', icon: 'XCircle' },
      'complete': { label: 'Complete', color: 'emerald', icon: 'CheckCircle' }
    };
    return displays[phase] || displays['ready'];
  }

  /**
   * Get display information for a calendar status
   */
  static getStatusDisplay(status) {
    const displays = {
      'planned': { label: 'Planned', color: 'gray', icon: 'Calendar' },
      'in_progress': { label: 'In Progress', color: 'azure', icon: 'Clock' },
      'completed': { label: 'Completed', color: 'emerald', icon: 'Check' }
    };
    return displays[status] || displays['planned'];
  }

  /**
   * Check if a task is considered "active" based on its status
   */
  static isTaskActive(task) {
    const activeWorkflowPhases = ['in_progress', 'review'];
    const activeStatuses = ['in_progress'];
    
    return (
      (task.workflow_phase && activeWorkflowPhases.includes(task.workflow_phase)) ||
      (task.status && activeStatuses.includes(task.status))
    );
  }

  /**
   * Check if a task is considered "complete" based on its status
   */
  static isTaskComplete(task) {
    return (
      task.workflow_phase === 'complete' ||
      task.status === 'completed' ||
      task.application_status === 'completed' ||
      task.application_status === 'submitted'
    );
  }

  /**
   * Get progress percentage based on workflow phase
   */
  static getProgressPercentage(workflowPhase) {
    const progressMap = {
      'ready': 0,
      'in_progress': 40,
      'review': 75,
      'blocked': 40,  // Same as in_progress but blocked
      'complete': 100
    };
    return progressMap[workflowPhase] || 0;
  }
}

export default StatusMappingService;